import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Building2, Save, Eye, EyeOff, Plus, Trash2, Upload, ExternalLink, Globe, Loader2,
  Briefcase, Users, Star, Image, LogOut, X, Pencil, MapPin, Newspaper, Check
} from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES } from "@/data/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : null;
};

export default function CompanyDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Form states
  const [form, setForm] = useState({});
  const [newService, setNewService] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [newCity, setNewCity] = useState("");

  // Project form
  const [projForm, setProjForm] = useState({ title: "", description: "", location: "", fair_name: "", stand_size: "", year: "", video_url: "" });
  const [projImages, setProjImages] = useState([]);
  const [editingProjectId, setEditingProjectId] = useState(null);

  // News
  const [companyNews, setCompanyNews] = useState({ news: [], total: 0 });
  const [newsPage, setNewsPage] = useState(1);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [newsForm, setNewsForm] = useState({ title: "", summary: "", content: "", image_url: "", tags: "" });
  const [newsGallery, setNewsGallery] = useState([]);
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Client form
  const [clientName, setClientName] = useState("");
  const [clientLogo, setClientLogo] = useState("");

  // Testimonial form
  const [testForm, setTestForm] = useState({ author: "", company: "", text: "", rating: 5 });

  const fetchProfile = useCallback(async () => {
    const auth = getAuth();
    if (!auth) { navigate("/giris"); return; }
    try {
      const res = await axios.get(`${API}/companies/me/profile`, auth);
      setProfile(res.data);
      setForm({
        company_name: res.data.company_name || "",
        about: res.data.about || "",
        logo_url: res.data.logo_url || "",
        cover_image_url: res.data.cover_image_url || "",
        contact_email: res.data.contact?.email || "",
        contact_phone: res.data.contact?.phone || "",
        contact_website: res.data.contact?.website || "",
        contact_address: res.data.contact?.address || "",
        contact_city: res.data.contact?.city || "",
        contact_country: res.data.contact?.country || "",
        social_linkedin: res.data.social?.linkedin || "",
        social_instagram: res.data.social?.instagram || "",
      });
    } catch {
      localStorage.removeItem("token");
      navigate("/giris");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const auth = getAuth();
      const res = await axios.put(`${API}/companies/me/profile`, {
        ...form,
        countries: profile.countries,
        services: profile.services,
        cities: profile.cities,
      }, auth);
      setProfile(res.data);
      toast.success("Profil kaydedildi!");
    } catch (e) {
      toast.error("Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  // NEWS
  const fetchNews = async (p = 1) => {
    const r = await axios.get(`${API}/companies/me/news?page=${p}`, getAuth());
    setCompanyNews(r.data); setNewsPage(p);
  };

  const saveNewsItem = async () => {
    if (!newsForm.title) { toast.error("Başlık gerekli"); return; }
    const payload = { ...newsForm, tags: newsForm.tags.split(",").map(t => t.trim()).filter(Boolean), gallery_images: newsGallery };
    try {
      if (editingNewsId) {
        await axios.put(`${API}/companies/me/news/${editingNewsId}`, payload, getAuth());
      } else {
        await axios.post(`${API}/companies/me/news`, payload, getAuth());
      }
      setNewsForm({ title: "", summary: "", content: "", image_url: "", tags: "" });
      setNewsGallery([]); setEditingNewsId(null); setShowNewsForm(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      fetchNews(newsPage);
    } catch { toast.error("Hata"); }
  };

  const togglePublish = async () => {
    try {
      const res = await axios.put(`${API}/companies/me/publish`, {}, getAuth());
      setProfile((p) => ({ ...p, is_published: res.data.is_published }));
      toast.success(res.data.is_published ? "Profil yayınlandı!" : "Profil yayından kaldırıldı");
    } catch {
      toast.error("Hata oluştu");
    }
  };

  const addService = () => {
    if (!newService.trim()) return;
    setProfile((p) => ({ ...p, services: [...(p.services || []), newService.trim()] }));
    setNewService("");
  };

  const removeService = (idx) => {
    setProfile((p) => ({ ...p, services: p.services.filter((_, i) => i !== idx) }));
  };

  const addCountry = () => {
    if (!newCountry.trim()) return;
    setProfile((p) => ({ ...p, countries: [...(p.countries || []), newCountry.trim()] }));
    setNewCountry("");
  };

  const removeCountry = (idx) => {
    setProfile((p) => ({ ...p, countries: p.countries.filter((_, i) => i !== idx) }));
  };

  const addCity = () => {
    if (!newCity.trim()) return;
    setProfile((p) => ({ ...p, cities: [...(p.cities || []), newCity.trim()] }));
    setNewCity("");
  };

  const removeCity = (idx) => {
    setProfile((p) => ({ ...p, cities: (p.cities || []).filter((_, i) => i !== idx) }));
  };

  const compressImage = (file, maxW = 800, q = 0.7) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > maxW) { h = (maxW / w) * h; w = maxW; }
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", q));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file, 1000, 0.75);
    callback(compressed);
  };

  const handleMultiImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 6 - projImages.length;
    if (remaining <= 0) { toast.error("Maksimum 6 görsel ekleyebilirsiniz"); return; }
    const toProcess = files.slice(0, remaining);
    for (const file of toProcess) {
      const compressed = await compressImage(file, 800, 0.65);
      setProjImages((prev) => prev.length < 6 ? [...prev, compressed] : prev);
    }
    if (files.length > remaining) toast.info(`Maksimum 6 görsel. ${files.length - remaining} dosya atlandı.`);
    e.target.value = "";
  };

  const addProject = async () => {
    if (!projForm.title) { toast.error("Proje başlığı gerekli"); return; }
    try {
      if (editingProjectId) {
        // UPDATE existing project
        const res = await axios.put(`${API}/companies/me/projects/${editingProjectId}`, { ...projForm, images: projImages }, getAuth());
        setProfile((p) => ({
          ...p,
          projects: p.projects.map((pr) => pr.id === editingProjectId ? res.data : pr)
        }));
        setEditingProjectId(null);
        toast.success("Proje güncellendi!");
      } else {
        // ADD new project
        const res = await axios.post(`${API}/companies/me/projects`, { ...projForm, images: projImages }, getAuth());
        setProfile((p) => ({ ...p, projects: [...(p.projects || []), res.data] }));
        toast.success("Proje eklendi!");
      }
      setProjForm({ title: "", description: "", location: "", fair_name: "", stand_size: "", year: "", video_url: "" });
      setProjImages([]);
    } catch { toast.error("Hata"); }
  };

  const startEditProject = (proj) => {
    setEditingProjectId(proj.id);
    setProjForm({
      title: proj.title || "",
      description: proj.description || "",
      location: proj.location || "",
      fair_name: proj.fair_name || "",
      stand_size: proj.stand_size || "",
      year: proj.year || "",
      video_url: proj.video_url || "",
    });
    setProjImages(proj.images || []);
    // Scroll to form
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const cancelEditProject = () => {
    setEditingProjectId(null);
    setProjForm({ title: "", description: "", location: "", fair_name: "", stand_size: "", year: "", images: [] });
    setProjImages([]);
  };

  const deleteProject = async (id) => {
    try {
      await axios.delete(`${API}/companies/me/projects/${id}`, getAuth());
      setProfile((p) => ({ ...p, projects: p.projects.filter((pr) => pr.id !== id) }));
      toast.success("Proje silindi");
    } catch { toast.error("Hata"); }
  };

  const addClient = async () => {
    if (!clientName) return;
    try {
      const res = await axios.post(`${API}/companies/me/clients`, { name: clientName, logo_url: clientLogo }, getAuth());
      setProfile((p) => ({ ...p, clients: [...(p.clients || []), res.data] }));
      setClientName(""); setClientLogo("");
      toast.success("Müşteri eklendi!");
    } catch { toast.error("Hata"); }
  };

  const deleteClient = async (id) => {
    try {
      await axios.delete(`${API}/companies/me/clients/${id}`, getAuth());
      setProfile((p) => ({ ...p, clients: p.clients.filter((c) => c.id !== id) }));
    } catch { toast.error("Hata"); }
  };

  const addTestimonial = async () => {
    if (!testForm.author || !testForm.text) { toast.error("Yazar ve yorum gerekli"); return; }
    try {
      const res = await axios.post(`${API}/companies/me/testimonials`, testForm, getAuth());
      setProfile((p) => ({ ...p, testimonials: [...(p.testimonials || []), res.data] }));
      setTestForm({ author: "", company: "", text: "", rating: 5 });
      toast.success("Yorum eklendi!");
    } catch { toast.error("Hata"); }
  };

  const deleteTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/companies/me/testimonials/${id}`, getAuth());
      setProfile((p) => ({ ...p, testimonials: p.testimonials.filter((t) => t.id !== id) }));
    } catch { toast.error("Hata"); }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/giris");
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  if (!profile) return null;

  return (
    <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-8" data-testid="company-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{profile.company_name}</h1>
          <p className="text-sm text-stone-500 mt-1">
            Profil URL: <span className="text-[#1B4F72] font-mono text-xs">fairstand.com/firma/{profile.slug}</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/firma/${profile.slug}`} target="_blank">
            <Button variant="outline" className="rounded-none font-mono text-[10px] uppercase tracking-widest gap-1.5" data-testid="preview-btn">
              <ExternalLink className="w-3.5 h-3.5" /> Profili Gör
            </Button>
          </Link>
          <Button onClick={togglePublish} variant={profile.is_published ? "default" : "outline"} className={`rounded-none font-mono text-[10px] uppercase tracking-widest gap-1.5 ${profile.is_published ? "bg-green-700 hover:bg-green-800" : ""}`} data-testid="publish-btn">
            {profile.is_published ? <><Eye className="w-3.5 h-3.5" /> Yayında</> : <><EyeOff className="w-3.5 h-3.5" /> Yayınla</>}
          </Button>
          <Button onClick={logout} variant="ghost" className="rounded-none font-mono text-[10px] uppercase tracking-widest gap-1.5 text-stone-400" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Çıkış
          </Button>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6" onValueChange={(v) => { if (v === "news") fetchNews(); }}>
        <TabsList className="bg-transparent border-b border-stone-200 rounded-none p-0 h-auto gap-0">
          {[
            { val: "profile", label: "Profil Bilgileri", icon: Building2 },
            { val: "news", label: "Haberler", icon: Newspaper },
            { val: "projects", label: "Projeler", icon: Briefcase },
            { val: "clients", label: "Müşteriler", icon: Users },
            { val: "testimonials", label: "Yorumlar", icon: Star },
          ].map((tab) => (
            <TabsTrigger key={tab.val} value={tab.val} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1B4F72] data-[state=active]:text-[#1B4F72] font-mono text-[10px] uppercase tracking-widest px-4 py-3 gap-1.5">
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* PROFILE TAB */}
        <TabsContent value="profile" className="space-y-6">
          <div className="bg-white border border-stone-200 p-6 space-y-5">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Temel Bilgiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Firma Adı</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-none mt-1" data-testid="input-company-name" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Logo URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." className="rounded-none flex-1" />
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setForm({ ...form, logo_url: url }))} />
                    <Button variant="outline" className="rounded-none" asChild><span><Upload className="w-4 h-4" /></span></Button>
                  </label>
                </div>
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Kapak Görseli URL</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} placeholder="https://..." className="rounded-none flex-1" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setForm({ ...form, cover_image_url: url }))} />
                  <Button variant="outline" className="rounded-none" asChild><span><Upload className="w-4 h-4" /></span></Button>
                </label>
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Hakkımızda</Label>
              <Textarea value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} rows={5} className="rounded-none mt-1" placeholder="Firmanız hakkında detaylı bilgi..." data-testid="input-about" />
            </div>
          </div>

          {/* Services */}
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Hizmetler</h3>
            <div className="flex flex-wrap gap-2">
              {(profile.services || []).map((s, i) => (
                <Badge key={i} variant="outline" className="rounded-none gap-1 pr-1">
                  {s} <button onClick={() => removeService(i)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newService} onChange={(e) => setNewService(e.target.value)} placeholder="Yeni hizmet ekle..." className="rounded-none flex-1" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addService())} />
              <Button onClick={addService} variant="outline" className="rounded-none"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Countries - Autocomplete */}
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Hizmet Verilen Ülkeler</h3>
            <div className="flex flex-wrap gap-2">
              {(profile.countries || []).map((c, i) => (
                <Badge key={i} variant="outline" className="rounded-none gap-1 pr-1">
                  <Globe className="w-3 h-3" />{c} <button onClick={() => removeCountry(i)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="relative">
              <Input
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                placeholder="Ülke adı yazın..."
                className="rounded-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addCountry(); }
                  if (e.key === "Escape") setNewCountry("");
                }}
                data-testid="country-input"
              />
              {newCountry.length >= 2 && (
                <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-stone-200 shadow-lg max-h-48 overflow-auto">
                  {COUNTRIES
                    .filter((c) => c.toLowerCase().includes(newCountry.toLowerCase()) && !(profile.countries || []).includes(c))
                    .slice(0, 8)
                    .map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          setProfile((p) => ({ ...p, countries: [...(p.countries || []), c] }));
                          setNewCountry("");
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-stone-50 hover:text-[#1B4F72] transition-colors flex items-center gap-2 border-b border-stone-100 last:border-0"
                        data-testid={`country-option-${c}`}
                      >
                        <Globe className="w-3.5 h-3.5 text-stone-400" />
                        {c}
                      </button>
                    ))}
                  {COUNTRIES.filter((c) => c.toLowerCase().includes(newCountry.toLowerCase()) && !(profile.countries || []).includes(c)).length === 0 && (
                    <p className="px-4 py-3 text-sm text-stone-400">Sonuç bulunamadı</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Cities */}
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Hizmet Verilen Şehirler</h3>
            <div className="flex flex-wrap gap-2">
              {(profile.cities || []).map((c, i) => (
                <Badge key={i} variant="outline" className="rounded-none gap-1 pr-1">
                  <MapPin className="w-3 h-3" />{c} <button onClick={() => removeCity(i)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="Şehir adı yazın ve Enter'a basın..." className="rounded-none flex-1" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCity())} data-testid="city-input" />
              <Button onClick={addCity} variant="outline" className="rounded-none"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">İletişim</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: "contact_email", label: "E-posta", ph: "firma@ornek.com" },
                { key: "contact_phone", label: "Telefon", ph: "+90 212 xxx xx xx" },
                { key: "contact_website", label: "Web Sitesi", ph: "https://www.firma.com" },
                { key: "contact_city", label: "Şehir", ph: "İstanbul" },
                { key: "contact_country", label: "Ülke", ph: "Türkiye" },
              ].map(({ key, label, ph }) => (
                <div key={key}>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">{label}</Label>
                  <Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph} className="rounded-none mt-1" />
                </div>
              ))}
              <div className="md:col-span-2">
                <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Adres</Label>
                <Input value={form.contact_address || ""} onChange={(e) => setForm({ ...form, contact_address: e.target.value })} placeholder="Tam adres" className="rounded-none mt-1" />
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Sosyal Medya</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">LinkedIn</Label><Input value={form.social_linkedin || ""} onChange={(e) => setForm({ ...form, social_linkedin: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Instagram</Label><Input value={form.social_instagram || ""} onChange={(e) => setForm({ ...form, social_instagram: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
          </div>

          <Button onClick={saveProfile} disabled={saving} className="rounded-none bg-stone-900 hover:bg-[#1B4F72] font-mono text-xs uppercase tracking-widest h-11 px-8 gap-2" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Profili Kaydet
          </Button>
        </TabsContent>

        {/* NEWS TAB */}
        <TabsContent value="news" className="space-y-6" onFocusCapture={() => { if (companyNews.news.length === 0) fetchNews(); }}>
          {/* Success Modal */}
          {showSuccess && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-lg p-8 text-center shadow-xl max-w-sm mx-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">Haber Başarıyla Kaydedildi!</h3>
                <p className="text-sm text-stone-500 mb-4">Haberiniz yayınlandı.</p>
                <Button onClick={() => setShowSuccess(false)} variant="outline" className="rounded-none"><X className="w-4 h-4 mr-1" /> Kapat</Button>
              </div>
            </div>
          )}

          {showNewsForm ? (
            /* NEWS FORM */
            <div className="bg-white border border-stone-200 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">{editingNewsId ? "Haberi Düzenle" : "Yeni Haber"}</h3>
                <Button onClick={() => { setShowNewsForm(false); setEditingNewsId(null); setNewsForm({ title: "", summary: "", content: "", image_url: "", tags: "" }); setNewsGallery([]); }} variant="ghost" size="sm" className="gap-1 text-stone-400"><X className="w-3.5 h-3.5" /> İptal</Button>
              </div>
              <div><Label className="text-xs text-stone-400">Başlık *</Label><Input value={newsForm.title} onChange={e => setNewsForm({...newsForm, title: e.target.value})} className="rounded-none mt-1 text-lg font-semibold" placeholder="Haber başlığı..." /></div>
              <div><Label className="text-xs text-stone-400">Kısa Özet</Label><Textarea value={newsForm.summary} onChange={e => setNewsForm({...newsForm, summary: e.target.value})} rows={2} className="rounded-none mt-1" placeholder="Ana sayfada görünecek özet..." /></div>
              <div><Label className="text-xs text-stone-400">Haber İçeriği</Label><Textarea value={newsForm.content} onChange={e => setNewsForm({...newsForm, content: e.target.value})} rows={8} className="rounded-none mt-1 font-mono text-sm" placeholder="Detaylı haber metni..." /></div>
              <div><Label className="text-xs text-stone-400">Etiketler (virgülle)</Label><Input value={newsForm.tags} onChange={e => setNewsForm({...newsForm, tags: e.target.value})} className="rounded-none mt-1" placeholder="fuar, stand, exhibition" /></div>
              <div><Label className="text-xs text-stone-400">Ana Görsel</Label>
                <div className="flex gap-2 mt-1"><Input value={newsForm.image_url} onChange={e => setNewsForm({...newsForm, image_url: e.target.value})} className="rounded-none flex-1" placeholder="URL veya yükle" />
                <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, url => setNewsForm({...newsForm, image_url: url}))} /><Button variant="outline" className="rounded-none" asChild><span><Upload className="w-4 h-4" /></span></Button></label></div>
                {newsForm.image_url && <img src={newsForm.image_url} alt="" className="h-24 mt-2 object-cover border" />}
              </div>
              <div><Label className="text-xs text-stone-400">Galeri (max 8)</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {newsGallery.map((img, i) => (<div key={i} className="aspect-[4/3] relative border"><img src={img} alt="" className="w-full h-full object-cover" /><button onClick={() => setNewsGallery(newsGallery.filter((_,j) => j!==i))} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"><X className="w-2.5 h-2.5" /></button></div>))}
                  {newsGallery.length < 8 && (<label className="aspect-[4/3] border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1B4F72]"><input type="file" accept="image/*" multiple className="hidden" onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    const rem = 8 - newsGallery.length;
                    for (const f of files.slice(0, rem)) { const c = await compressImage(f, 800, 0.65); setNewsGallery(p => p.length < 8 ? [...p, c] : p); }
                    e.target.value = "";
                  }} /><Plus className="w-5 h-5 text-stone-400" /><span className="text-[9px] text-stone-400">{newsGallery.length}/8</span></label>)}
                </div>
              </div>
              <Button onClick={saveNewsItem} className="rounded-none bg-[#1B4F72] hover:bg-[#154360] font-mono text-xs uppercase tracking-widest gap-2">{editingNewsId ? <><Save className="w-4 h-4" /> Güncelle</> : <><Save className="w-4 h-4" /> Kaydet ve Yayınla</>}</Button>
            </div>
          ) : (
            /* NEWS LIST */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Haberleriniz ({companyNews.total})</h3>
                <Button onClick={() => { setShowNewsForm(true); fetchNews(); }} className="rounded-none bg-[#1B4F72] hover:bg-[#154360] font-mono text-[10px] uppercase tracking-widest gap-1.5" data-testid="new-news-btn">
                  <Plus className="w-3.5 h-3.5" /> Yeni Haber
                </Button>
              </div>
              {companyNews.news.length > 0 ? (
                <div className="space-y-2">
                  {companyNews.news.map(n => (
                    <div key={n.id} className="bg-white border border-stone-200 p-4 flex gap-4 hover:border-stone-300 transition-colors">
                      {n.image_url && <img src={n.image_url} alt="" className="w-24 h-16 object-cover flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm text-stone-900 truncate">{n.title}</h4>
                        <p className="text-xs text-stone-500 line-clamp-1 mt-0.5">{n.summary}</p>
                        <span className="text-[10px] text-stone-400 font-mono">{new Date(n.created_at).toLocaleDateString("tr-TR")}</span>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button onClick={() => { setEditingNewsId(n.id); setNewsForm({ title: n.title, summary: n.summary||"", content: n.content||"", image_url: n.image_url||"", tags: (n.tags||[]).join(", ") }); setNewsGallery(n.gallery_images||[]); setShowNewsForm(true); }} variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-[#1B4F72]"><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button onClick={async () => { await axios.delete(`${API}/companies/me/news/${n.id}`, getAuth()); fetchNews(newsPage); toast.success("Silindi"); }} variant="ghost" size="icon" className="h-7 w-7 text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                  {companyNews.total > 5 && (
                    <div className="flex justify-center gap-2 mt-4">
                      <Button onClick={() => fetchNews(newsPage-1)} disabled={newsPage<=1} variant="outline" size="sm" className="rounded-none">Önceki</Button>
                      <span className="text-xs text-stone-500 py-2">{newsPage}/{Math.ceil(companyNews.total/5)}</span>
                      <Button onClick={() => fetchNews(newsPage+1)} disabled={newsPage>=Math.ceil(companyNews.total/5)} variant="outline" size="sm" className="rounded-none">Sonraki</Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 bg-white border border-stone-200">
                  <Newspaper className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-400 mb-4">Henüz haber eklemediniz</p>
                  <Button onClick={() => setShowNewsForm(true)} variant="outline" className="rounded-none gap-1.5"><Plus className="w-3.5 h-3.5" /> İlk Haberinizi Ekleyin</Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* PROJECTS TAB */}
        <TabsContent value="projects" className="space-y-6">
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">
                {editingProjectId ? "Projeyi Düzenle" : "Yeni Proje Ekle"}
              </h3>
              {editingProjectId && (
                <Button onClick={cancelEditProject} variant="ghost" className="rounded-none font-mono text-[10px] uppercase tracking-widest text-stone-400 gap-1">
                  <X className="w-3.5 h-3.5" /> İptal
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Proje Başlığı *</Label><Input value={projForm.title} onChange={(e) => setProjForm({ ...projForm, title: e.target.value })} className="rounded-none mt-1" placeholder="Stand 50m2 - Gulfood 2026" data-testid="project-title" /></div>
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Fuar Adı</Label><Input value={projForm.fair_name} onChange={(e) => setProjForm({ ...projForm, fair_name: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Konum</Label><Input value={projForm.location} onChange={(e) => setProjForm({ ...projForm, location: e.target.value })} className="rounded-none mt-1" placeholder="Dubai, UAE" /></div>
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Stand Boyutu</Label><Input value={projForm.stand_size} onChange={(e) => setProjForm({ ...projForm, stand_size: e.target.value })} className="rounded-none mt-1" placeholder="50 m²" /></div>
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Yıl</Label><Input value={projForm.year} onChange={(e) => setProjForm({ ...projForm, year: e.target.value })} className="rounded-none mt-1" placeholder="2026" /></div>
            </div>
            <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Açıklama</Label><Textarea value={projForm.description} onChange={(e) => setProjForm({ ...projForm, description: e.target.value })} rows={3} className="rounded-none mt-1" /></div>
            {/* Video URL */}
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Video Linki (YouTube / Vimeo)</Label>
              <Input value={projForm.video_url || ""} onChange={(e) => setProjForm({ ...projForm, video_url: e.target.value })} className="rounded-none mt-1" placeholder="https://www.youtube.com/watch?v=..." data-testid="project-video-url" />
              {projForm.video_url && projForm.video_url.includes("youtu") && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={`https://img.youtube.com/vi/${projForm.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)?.[1]}/default.jpg`} alt="" className="w-20 h-14 object-cover" />
                  <span className="text-xs text-green-600 font-mono">Video bağlantısı geçerli</span>
                </div>
              )}
            </div>
            {/* Multi-image upload */}
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Proje Görselleri (max 6)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {projImages.map((img, i) => (
                  <div key={i} className="w-24 h-24 relative border border-stone-200">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setProjImages(projImages.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                {projImages.length < 6 && (
                  <label className="w-24 h-24 border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1B4F72] transition-colors">
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handleMultiImageUpload} />
                    <Plus className="w-5 h-5 text-stone-400" />
                    <span className="text-[9px] text-stone-400 mt-1">{projImages.length}/6</span>
                  </label>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={addProject} className={`rounded-none font-mono text-xs uppercase tracking-widest gap-2 ${editingProjectId ? "bg-[#1B4F72] hover:bg-[#154360]" : "bg-stone-900 hover:bg-[#1B4F72]"}`} data-testid="add-project-btn">
                {editingProjectId ? <><Save className="w-4 h-4" /> Güncelle</> : <><Plus className="w-4 h-4" /> Proje Ekle</>}
              </Button>
              {editingProjectId && (
                <Button onClick={cancelEditProject} variant="outline" className="rounded-none font-mono text-xs uppercase tracking-widest">İptal</Button>
              )}
            </div>
          </div>

          {/* Existing projects */}
          {(profile.projects || []).length > 0 && (
            <div className="space-y-4">
              <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Mevcut Projeler ({profile.projects.length})</h3>
              {profile.projects.map((p) => (
                <div key={p.id} className={`bg-white border p-4 flex gap-4 transition-colors ${editingProjectId === p.id ? "border-[#1B4F72] bg-red-50/30" : "border-stone-200"}`}>
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-24 h-20 object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-stone-900">{p.title}</h4>
                    <p className="text-xs text-stone-500">{[p.fair_name, p.location, p.stand_size, p.year].filter(Boolean).join(" · ")}</p>
                    {p.images?.length > 0 && <p className="text-[10px] text-stone-400 mt-1">{p.images.length} görsel</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button onClick={() => startEditProject(p)} variant="ghost" size="icon" className="text-stone-400 hover:text-[#1B4F72]" data-testid={`edit-project-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => deleteProject(p.id)} variant="ghost" size="icon" className="text-stone-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CLIENTS TAB */}
        <TabsContent value="clients" className="space-y-6">
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Müşteri Ekle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Müşteri Adı *</Label><Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="rounded-none mt-1" data-testid="client-name" /></div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Logo (PNG)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={clientLogo} onChange={(e) => setClientLogo(e.target.value)} placeholder="URL veya yükle" className="rounded-none flex-1" />
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => handleImageUpload(e, setClientLogo)} />
                    <Button variant="outline" className="rounded-none" asChild><span><Upload className="w-4 h-4" /></span></Button>
                  </label>
                </div>
              </div>
            </div>
            <Button onClick={addClient} className="rounded-none bg-stone-900 hover:bg-[#1B4F72] font-mono text-xs uppercase tracking-widest gap-2" data-testid="add-client-btn">
              <Plus className="w-4 h-4" /> Müşteri Ekle
            </Button>
          </div>

          {(profile.clients || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {profile.clients.map((c) => (
                <div key={c.id} className="bg-white border border-stone-200 p-3 flex flex-col items-center relative group">
                  {c.logo_url ? <img src={c.logo_url} alt={c.name} className="w-16 h-16 object-contain" /> : <span className="text-sm text-stone-500">{c.name}</span>}
                  <span className="text-xs text-stone-500 mt-1">{c.name}</span>
                  <button onClick={() => deleteClient(c.id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TESTIMONIALS TAB */}
        <TabsContent value="testimonials" className="space-y-6">
          <div className="bg-white border border-stone-200 p-6 space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 font-bold">Yorum Ekle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Yazar Adı *</Label><Input value={testForm.author} onChange={(e) => setTestForm({ ...testForm, author: e.target.value })} className="rounded-none mt-1" data-testid="testimonial-author" /></div>
              <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Firma</Label><Input value={testForm.company} onChange={(e) => setTestForm({ ...testForm, company: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Yorum Metni *</Label><Textarea value={testForm.text} onChange={(e) => setTestForm({ ...testForm, text: e.target.value })} rows={3} className="rounded-none mt-1" data-testid="testimonial-text" /></div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Puan</Label>
              <div className="flex gap-1 mt-1">{[1,2,3,4,5].map((s) => <button key={s} onClick={() => setTestForm({ ...testForm, rating: s })}><Star className={`w-5 h-5 ${s <= testForm.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-stone-200"}`} /></button>)}</div>
            </div>
            <Button onClick={addTestimonial} className="rounded-none bg-stone-900 hover:bg-[#1B4F72] font-mono text-xs uppercase tracking-widest gap-2" data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4" /> Yorum Ekle
            </Button>
          </div>

          {(profile.testimonials || []).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.testimonials.map((t) => (
                <div key={t.id} className="bg-white border border-stone-200 p-4 relative group">
                  <p className="text-sm italic text-stone-700">"{t.text}"</p>
                  <p className="text-xs font-semibold mt-2">{t.author} {t.company && <span className="text-stone-400">— {t.company}</span>}</p>
                  <button onClick={() => deleteTestimonial(t.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
